import { useSoftDeleteChatMessage, useUpdateMessageContent } from "@/orval/gen"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { CancelButton, DeleteButton, SubmitButton } from "@components/Buttons"
import { FormButtonGroup } from "@components/form-button-group"
import { Modal, ModalProps, Stack } from "@mantine/core"
import { useForm } from "@mantine/form"
import { useTranslation } from "react-i18next"
import { TextareaWithEmojiPicker } from "./TextareaWithEmojiPicker"
import { DefaultModal } from "@components/default-modal"

interface Props extends ModalProps {
  postId: number
  message: string
}

function UpdateMessageModalContent({ message, postId, ...modalProps }: Props) {
  const { t } = useTranslation("app_server")
  const { onClose } = modalProps
  const form = useForm({ initialValues: { newMessage: message } })
  const { mutateAsync: updateMessage } = useUpdateMessageContent({
    mutation: {
      onError: showErrorNotification,
      onSuccess: () => {
        onClose()
      },
    },
  })
  const handleSubmit = async (formValues: typeof form.values) => {
    await updateMessage({ data: { ...formValues, chatPostId: postId } })
  }
  const { mutateAsync: deleteMessage } = useSoftDeleteChatMessage({
    mutation: {
      onError: showErrorNotification,
      onSuccess: () => {
        onClose()
      },
    },
  })
  const handleClickDelete = async () => {
    await deleteMessage({ params: { messageId: postId } })
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <TextareaWithEmojiPicker
          key={form.key("newMessage")}
          value={form.getValues().newMessage}
          onChange={(v) => form.setFieldValue("newMessage", v)}
        />
        <FormButtonGroup>
          <DeleteButton onClick={handleClickDelete} />
          <CancelButton onClick={onClose} />
          <SubmitButton>{t("Update")}</SubmitButton>
        </FormButtonGroup>
      </Stack>
    </form>
  )
}

export function UpdateMessageModal({ postId, message, ...modalProps }: Props) {
  const { t } = useTranslation("app_server")
  return (
    <DefaultModal {...modalProps} title={t("EditMessage")}>
      <UpdateMessageModalContent
        {...modalProps}
        postId={postId}
        message={message}
      />
    </DefaultModal>
  )
}
